/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LmsShell, lmsApi, getStudentToken, clearStudentSession } from "./shell";
import { PracticeBanner } from "./PracticeBanner";
import { ArrowLeft, Award, LogOut, Timer, Send, Trophy } from "lucide-react";

/**
 * Practice exam engine: 50 Qs, 60 minutes, 80% pass, 90-minute cooldown
 * between attempts (the real TC exam requires 24 hours; we're deliberately
 * looser for study flow — see /groundschool § 03·B).
 * Route: /dashboard/exam
 */
export default function ExamEngine() {
  const nav = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | intro | running | results | error | cooldown
  const [attempt, setAttempt] = useState(null); // ExamStartResponse
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [cooldownMsg, setCooldownMsg] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const submitRef = useRef(false);

  useEffect(() => {
    if (!getStudentToken()) { nav("/login", { replace: true }); return; }
    (async () => {
      try {
        const prog = await lmsApi.progress();
        const p0 = (prog && prog[0]) || null;
        if (p0 && p0.exam_can_start === false) {
          setCooldownMsg(
            p0.exam_next_allowed_at
              ? `Cooldown active — next attempt allowed at ${new Date(p0.exam_next_allowed_at).toLocaleString()}.`
              : "Cooldown active."
          );
          setPhase("cooldown");
          return;
        }
        setPhase("intro");
      } catch (e) {
        if (e.status === 401) { clearStudentSession(); nav("/login", { replace: true }); return; }
        setErr(e.message || "Failed to load");
        setPhase("error");
      }
    })();
  }, [nav]);

  useEffect(() => {
    if (phase !== "running") return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [phase]);

  const startExam = async () => {
    try {
      const r = await lmsApi.startExam();
      setAttempt(r);
      setAnswers({});
      setPhase("running");
    } catch (e) {
      if (e.status === 429) {
        setCooldownMsg(e.message || "Cooldown active");
        setPhase("cooldown");
        return;
      }
      setErr(e.message || "Failed to start exam");
      setPhase("error");
    }
  };

  const submit = async (isAuto = false) => {
    if (submitRef.current) return;
    submitRef.current = true;
    try {
      const r = await lmsApi.submitExam(attempt.attempt_id, answers);
      setResult(r);
      setPhase("results");
    } catch (e) { setErr(e.message || "Submit failed"); setPhase("error"); }
    finally { submitRef.current = false; }
  };

  // Countdown & auto-submit
  const remainingMs = useMemo(() => {
    if (!attempt) return 0;
    return Math.max(0, new Date(attempt.deadline).getTime() - now);
  }, [attempt, now]);

  useEffect(() => {
    if (phase === "running" && attempt && remainingMs === 0) {
      submit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingMs, phase]);

  const signOut = () => { clearStudentSession(); nav("/login", { replace: true }); };
  const rightSlot = (
    <button onClick={signOut} className="tbg-btn ghost" style={{ padding: "6px 12px" }} data-testid="exam-signout">
      <LogOut size={12} /> Sign out
    </button>
  );

  return (
    <LmsShell subtitle="Practice Exam" rightSlot={rightSlot} wide>
      <div className="tbg-lms-wide" data-testid="exam-engine">
        {phase === "running" && (
          <PracticeBanner variant="exam" testid="practice-banner-exam" />
        )}
        {phase !== "running" && <PracticeBanner />}

        {phase !== "running" && phase !== "results" && (
          <div style={{ marginBottom: 14 }}>
            <Link to="/dashboard" className="tbg-btn ghost" style={{ padding: "6px 12px" }} data-testid="exam-back">
              <ArrowLeft size={12} /> Back to dashboard
            </Link>
          </div>
        )}

        {phase === "loading" && <div className="tbg-panel">Loading…</div>}

        {phase === "intro" && (
          <div className="tbg-panel" data-testid="exam-intro">
            <div className="tbg-section-sub">Practice Exam</div>
            <h1 className="tbg-section-title">Full-length practice attempt</h1>
            <p style={{ color: "#a5b8b3", lineHeight: 1.6, marginTop: 8 }}>
              This mirrors the format of the Transport Canada Small Advanced Exam so
              you can build stamina and time discipline. When you click Start, the
              60-minute countdown begins immediately and cannot be paused.
            </p>
            <ul style={{ color: "#cfdad7", lineHeight: 1.6, marginTop: 12, paddingLeft: 20, fontSize: 13 }}>
              <li><b>50 questions</b> · pass mark <b>80%</b></li>
              <li><b>60 minutes</b> total · timer auto-submits at zero</li>
              <li>Randomized questions across all modules</li>
              <li>Retake allowed after a <b>90-minute cooldown</b> <span style={{ color: "#7b8b87", fontWeight: 400 }}>· the real TC exam requires 24 h</span></li>
              <li>Full review + rationale after submission</li>
            </ul>
            <div className="tbg-actionbar">
              <div style={{ font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87" }}>
                REMINDER · This is prep material, not the official TC exam.
              </div>
              <button className="tbg-btn" onClick={startExam} data-testid="exam-start">
                <Timer size={13} /> Start 60-min exam
              </button>
            </div>
          </div>
        )}

        {phase === "cooldown" && (
          <div className="tbg-panel" data-testid="exam-cooldown">
            <div className="tbg-section-sub">Cooldown</div>
            <h1 className="tbg-section-title">Not yet</h1>
            <p style={{ color: "#a5b8b3", lineHeight: 1.6, marginTop: 8 }}>{cooldownMsg}</p>
            <p style={{ color: "#a5b8b3", lineHeight: 1.6, marginTop: 8, fontSize: 13 }}>
              Our practice exam enforces a <b>90-minute cooldown</b> between attempts —
              enough time to review the rationale for the ones you missed and
              revisit the modules where you scored below 80%.
            </p>
            <p style={{ color: "#7b8b87", lineHeight: 1.6, marginTop: 8, fontSize: 12 }}>
              Note · The real Transport Canada Small Advanced Exam requires a
              full <b>24-hour</b> rewrite waiting period. This tool is looser by
              design for study flow.
            </p>
            <Link to="/dashboard" className="tbg-btn" style={{ marginTop: 16 }}>
              <ArrowLeft size={13} /> Back to modules
            </Link>
          </div>
        )}

        {phase === "error" && (
          <div className="tbg-panel" data-testid="exam-error">
            <div className="tbg-lms-err">{err}</div>
            <Link to="/dashboard" className="tbg-btn" style={{ marginTop: 12 }}>Back to dashboard</Link>
          </div>
        )}

        {phase === "running" && attempt && (
          <>
            <ExamHeader
              answered={Object.keys(answers).length}
              total={attempt.questions.length}
              remainingMs={remainingMs}
            />
            {attempt.warning && (
              <div style={{ margin: "12px 0", padding: "10px 12px", background: "rgba(240,190,60,0.08)",
                border: "1px solid rgba(240,190,60,0.35)", color: "#f0be3c", borderRadius: 4, fontSize: 12 }}>
                {attempt.warning}
              </div>
            )}
            {attempt.questions.map((q, i) => (
              <div key={q.id} className="tbg-qcard" data-testid={`exam-q-${i + 1}`}>
                <div className="tbg-qnum">Question {i + 1} of {attempt.questions.length}</div>
                <p className="tbg-qtext">
                  {q.is_placeholder && <span className="tbg-placeholder-tag" title="Placeholder">SAMPLE</span>}
                  {q.prompt}
                </p>
                {q.options.map((opt, idx) => (
                  <label
                    key={idx}
                    className={`tbg-qopt${answers[q.id] === idx ? " selected" : ""}`}
                    data-testid={`exam-q-${i + 1}-opt-${idx}`}
                  >
                    <input
                      type="radio" name={`eq-${q.id}`}
                      checked={answers[q.id] === idx}
                      onChange={() => setAnswers((a) => ({ ...a, [q.id]: idx }))}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            ))}
            <div className="tbg-actionbar">
              <div style={{ font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87" }}>
                Answered {Object.keys(answers).length} / {attempt.questions.length}
              </div>
              <button className="tbg-btn" onClick={() => submit(false)} data-testid="exam-submit">
                <Send size={13} /> Submit exam
              </button>
            </div>
            {err && <div className="tbg-lms-err" style={{ marginTop: 10 }}>{err}</div>}
          </>
        )}

        {phase === "results" && result && (
          <ExamResults result={result} />
        )}
      </div>
    </LmsShell>
  );
}

function ExamHeader({ answered, total, remainingMs }) {
  const mins = Math.floor(remainingMs / 60000);
  const secs = Math.floor((remainingMs % 60000) / 1000);
  const mm = String(mins).padStart(2, "0");
  const ss = String(secs).padStart(2, "0");
  const cls = remainingMs < 60_000 ? "critical" : (remainingMs < 300_000 ? "warn" : "");
  return (
    <div style={{
      position: "sticky", top: 92, zIndex: 15,
      display: "flex", justifyContent: "space-between", alignItems: "center",
      background: "#050807", padding: "12px 0", marginBottom: 12,
      borderBottom: "1px solid #1c2a27",
    }}>
      <div style={{ font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87", letterSpacing: "0.1em" }}>
        Answered {answered} / {total}
      </div>
      <div className={`tbg-timer ${cls}`} data-testid="exam-timer">
        <Timer size={13} /> {mm}:{ss}
      </div>
    </div>
  );
}

export function ExamResults({ result }) {
  return (
    <div data-testid="exam-results">
      {/* Success moment on pass — big congratulatory banner + prominent
          CTAs that route the student to (a) their dashboard where the
          certificate download appears and (b) the modules view. Only
          renders when they actually passed; failing attempts still get
          the score summary + full review below. */}
      {result.passed && (
        <div
          data-testid="exam-pass-banner"
          style={{
            border: "1px solid rgba(60,196,172,0.55)",
            background: "linear-gradient(135deg, rgba(60,196,172,0.10) 0%, rgba(60,196,172,0.02) 60%)",
            borderRadius: 8,
            padding: "28px 26px",
            marginBottom: 22,
            boxShadow: "0 8px 30px rgba(60,196,172,0.10)",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <Trophy size={26} style={{ color: "#3CC4AC", flexShrink: 0 }} />
            <div style={{
              font: "700 11px 'JetBrains Mono', monospace",
              letterSpacing: "0.28em", textTransform: "uppercase",
              color: "#3CC4AC",
            }}>
              Certificate Earned
            </div>
          </div>
          <h1 style={{
            font: "800 32px/1.15 'Montserrat','Geist',sans-serif",
            letterSpacing: "-0.015em",
            color: "#e2ece9",
            margin: "0 0 10px",
          }}>
            Congratulations — you passed!
          </h1>
          <p style={{ color: "#a5b8b3", lineHeight: 1.55, fontSize: 15, maxWidth: 620, margin: 0 }}>
            You scored <b style={{ color: "#3CC4AC" }}>{result.score_pct}%</b> ({result.correct} of {result.total} correct) —
            above the {result.pass_pct}% pass mark. Your Advanced RPAS Ground
            School completion certificate has been issued and is ready to
            download from your dashboard.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 20 }}>
            <Link
              to="/dashboard"
              className="tbg-btn"
              data-testid="exam-pass-cta-certificate"
              style={{
                background: "#3CC4AC", color: "#050807",
                borderColor: "#3CC4AC", fontWeight: 700,
              }}>
              <Award size={13} /> View your certificate
            </Link>
            <Link
              to="/dashboard"
              className="tbg-btn ghost"
              data-testid="exam-pass-cta-modules">
              <ArrowLeft size={13} /> Back to modules
            </Link>
          </div>
          <div style={{
            marginTop: 16,
            font: "500 11px 'JetBrains Mono', monospace",
            color: "#7b8b87", letterSpacing: "0.08em",
          }}>
            REMINDER · The real Transport Canada Small Advanced Exam is written
            separately on the TC Drone Management Portal.
          </div>
        </div>
      )}

      <div className="tbg-score" style={{ marginBottom: 20 }}>
        <div className={`pct ${result.passed ? "" : "fail"}`} data-testid="exam-score-pct">{result.score_pct}%</div>
        <div className="lbl">{result.passed ? "Practice Pass" : "Not yet"}</div>
        <div className="msg">
          {result.correct} of {result.total} correct · Pass mark {result.pass_pct}%
          {result.time_expired && " · Time expired"}
        </div>
        <div className="msg" style={{ maxWidth: 500 }}>
          {result.passed
            ? "Great pacing. Practicing to this standard means you're tracking well for the real Transport Canada exam. Remember — the real exam is written on the TC Drone Management Portal."
            : "Below 80%. Focus on the modules where you missed the most, then take another attempt after the 90-minute cooldown."}
        </div>
        <div className="msg" style={{ font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87", letterSpacing: "0.08em", marginTop: 12 }}>
          Next attempt allowed: {result.next_attempt_at ? new Date(result.next_attempt_at).toLocaleString() : "—"}
        </div>
      </div>

      <div className="tbg-section-sub">Full review</div>
      {result.results.map((r, i) => (
        <div key={r.id} className="tbg-qcard" data-testid={`exam-review-${i + 1}`}>
          <div className="tbg-qnum">Question {i + 1}</div>
          <p className="tbg-qtext">{r.prompt}</p>
          {r.options.map((opt, idx) => {
            let cls = "tbg-qopt";
            if (idx === r.correct_index) cls += " correct";
            else if (idx === r.your_index && idx !== r.correct_index) cls += " wrong";
            return (
              <div key={idx} className={cls}>
                <input type="radio" disabled checked={idx === r.your_index} readOnly /> {opt}
              </div>
            );
          })}
          {r.rationale && (
            <div className="tbg-rationale">
              <strong style={{ color: "#3dc0af", letterSpacing: "0.06em", fontSize: 11, textTransform: "uppercase" }}>Why: </strong>
              {r.rationale}
            </div>
          )}
        </div>
      ))}

      <div className="tbg-actionbar">
        <Link to="/dashboard" className="tbg-btn ghost" data-testid="exam-results-dashboard">
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
